import { useEffect, useState } from 'react'
import { ArrowUpRight, ChevronDown, Play, Route, X } from 'lucide-react'
import { motion } from 'motion/react'
import type { ReplaySource } from './RowReplayPreview'
import TaskEventReplay from './TaskEventReplay'
import { Link } from './nav'
import { useAdminMode } from '../hooks/useAdminMode'
import './OverviewExplorations.css'
import './JourneyReplayHero.css'

// Spring used for the grid→cover morph, so the shared element travels with consistent physics.
const HERO_SPRING = { type: 'spring', stiffness: 320, damping: 34, mass: 0.9 } as const

// Inline player for a journey's latest replay, pinned to the top like a banner.
//
// The player wrapper is a `motion.div` tagged with `layoutId={`journey-hero-${journeyId}`}` — the
// *same* id used by a matching journey card's thumbnail, enabling a shared-layout morph into this
// hero. Embedded players stream and play inline. Full-page headers start collapsed;
// expanding the cover enables playback and exposes the timeline controls.
//
// `source` may be null while the page is still loading (or when there's no replay to show). In that
// case a placeholder box still renders — tagged with the same `layoutId` and showing `placeholder` —
// so a card→hero morph has a stable target from the very first paint. Without this, the detail
// page would mount a skeleton with no matching `layoutId`, Motion would lose the shared element, and
// the open/close hero animation would break.
export default function JourneyReplayHero({
  journeyId,
  source,
  taskHref,
  placeholder,
  layoutId: layoutIdProp,
  header = false,
  title = 'Journey replay',
}: {
  journeyId: string
  source: ReplaySource | null
  taskHref?: string | null
  placeholder?: React.ReactNode
  /** Override the shared-layout id. Defaults to `journey-hero-${journeyId}` (matching the grid
   *  card). Callers that mount more than one hero for the same journey on a page (e.g. the Versions
   *  tab, where a journey can appear under multiple versions) pass a namespaced id to avoid clashes. */
  layoutId?: string
  header?: boolean
  title?: string
}) {
  const adminMode = useAdminMode()
  const layoutId = layoutIdProp ?? `journey-hero-${journeyId}`
  const [expanded, setExpanded] = useState(false)
  const [playRequestId, setPlayRequestId] = useState(0)

  useEffect(() => {
    if (!header || !expanded || !source) return
    const timeoutId = window.setTimeout(() => {
      setPlayRequestId(request => request + 1)
    }, 1500)
    return () => window.clearTimeout(timeoutId)
  }, [expanded, header, source])

  if (header) {
    const replayId = `journey-replay-${journeyId}`
    return (
      <>
      {taskHref && adminMode && (
        <div className="task-replay__header">
          <Link to={taskHref} className="task-replay__task-link" target="_blank" rel="noopener noreferrer" data-testid="journey-hero-task-link" data-analytics-event="replay-task-link">
            View task page <ArrowUpRight size={13} aria-hidden />
          </Link>
        </div>
      )}
      <motion.section
        layoutId={layoutId}
        className={`ov-replay-header journey-hero-header${expanded ? ' ov-replay-header--expanded' : ''}`}
        data-testid="journey-hero-player"
        transition={HERO_SPRING}
      >
        <div className="ov-replay-header__stage" id={replayId}>
          {source ? (
            <TaskEventReplay
              source={source}
              taskHref={taskHref}
              active
              fitHeight={!expanded}
              fit="cover"
              playRequestId={playRequestId}
              playbackEnabled={expanded}
            />
          ) : (
            <div className="journey-hero-header__placeholder">{placeholder}</div>
          )}
        </div>
        {!expanded && source && (
          <button
            type="button"
            className="ov-replay-header__play"
            aria-label="Expand and play journey replay"
            data-testid="journey-hero-play"
            onClick={() => setExpanded(true)}
          >
            <Play size={28} fill="currentColor" aria-hidden />
          </button>
        )}
        {expanded && (
          <button
            type="button"
            className="ov-replay-header__close"
            aria-label="Close journey replay"
            data-testid="journey-hero-close"
            onClick={() => setExpanded(false)}
          >
            <X size={14} aria-hidden />
          </button>
        )}
        {!expanded && (
          <div className="ov-replay-header__chrome">
            <button
              type="button"
              className="ov-replay-header__toggle"
              onClick={() => source && setExpanded(true)}
              aria-expanded={false}
              aria-controls={replayId}
              disabled={!source}
              data-testid="journey-hero-toggle"
            >
              <span className="ov-replay-header__title">
                <Route size={18} strokeWidth={1.75} aria-hidden />
                <span>
                  <strong>{title}</strong>
                  <small>{source ? 'Expand replay' : 'Replay unavailable'}</small>
                </span>
              </span>
              {source && <ChevronDown size={20} className="ov-replay-header__expand-icon" aria-hidden />}
            </button>
          </div>
        )}
      </motion.section>
      </>
    )
  }

  if (!source) {
    // No replay yet (loading / none / restricted): a 16:9 box carrying the same `layoutId` so the
    // morph still has a stable target, showing the placeholder content.
    return (
      <div className="journey-hero__sizer">
        <motion.div
          layoutId={layoutId}
          className="journey-hero__cover journey-hero__cover--empty"
          transition={HERO_SPRING}
          style={{ borderRadius: 10 }}
        >
          {placeholder}
        </motion.div>
      </div>
    )
  }

  return (
    <motion.div
      layoutId={layoutId}
      className="journey-hero__player-wrap"
      data-testid="journey-hero-player"
      transition={HERO_SPRING}
      style={{ borderRadius: 14 }}
    >
      <TaskEventReplay source={source} taskHref={taskHref} active />
    </motion.div>
  )
}
